import { ActionIcon, Button, Chip, Group, Popover, Slider, Stack, Text, Tooltip } from '@mantine/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { IconVolume, IconVolumeOff } from '@tabler/icons-react';
import type { Settings } from '@flow/core';
import { useDeps } from '../../app/services';
import { alertKind, timerBeep, unlockAudio } from '../../infra/browser';

/** Seconds left the judge can pick from. 0 = time's up. */
const CHOICES = [120, 60, 30, 15, 10, 5, 0];
const label = (s: number) => (s === 0 ? "Time's up" : s >= 60 ? `${s / 60}:00` : `${s}s`);
const SOUND_NAMES = { end: 'alarm', short: 'quick pips', long: 'chime' };

/** Pick when the timer beeps and how loud. Every change plays what it will sound like (and unlocks audio on iPad). */
export const TimerAlertsEditor = ({ spacing = 'sm' }: { spacing?: 'sm' | 'xl' } = {}) => {
  const deps = useDeps();
  const settings = useLiveQuery(() => deps.settings.get(), []);
  if (!settings) return null;
  const save = async (patch: Partial<Settings>) => deps.settings.put({ ...(await deps.settings.get()), ...patch });

  return (
    <Stack gap={spacing}>
      <div>
        <Text size="sm" mb={6}>Beep at (time left)</Text>
        <Chip.Group
          multiple
          value={settings.timerAlertsSec.map(String)}
          onChange={(v) => {
            unlockAudio();
            const next = v.map(Number).sort((a, b) => b - a);
            const added = next.find((s) => !settings.timerAlertsSec.includes(s));
            if (added !== undefined) timerBeep(added, settings.timerVolume);
            void save({ timerAlertsSec: next });
          }}
        >
          <Group gap={6}>
            {CHOICES.map((s) => <Chip key={s} value={String(s)} size="xs">{label(s)}</Chip>)}
          </Group>
        </Chip.Group>
        <Text size="xs" c="dimmed" mt={6}>Different sounds: {SOUND_NAMES.long} above 10s, {SOUND_NAMES.short} at 10s or less, {SOUND_NAMES.end} at time's up.</Text>
      </div>
      <div>
        <Text size="sm" mb={6}>Volume</Text>
        <Group gap="sm" wrap="nowrap">
          <Slider
            style={{ flex: 1 }}
            min={0.1}
            max={1}
            step={0.05}
            value={settings.timerVolume}
            label={(v) => `${Math.round(v * 100)}%`}
            onChange={(v) => void save({ timerVolume: v })}
            onChangeEnd={(v) => { unlockAudio(); timerBeep(0, v); }}
            aria-label="Timer volume"
          />
        </Group>
      </div>
      <Group gap={6}>
        <Text size="xs" c="dimmed">Test:</Text>
        {([30, 10, 0] as const).map((s) => (
          <Button key={s} size="compact-xs" variant="default" onClick={() => { unlockAudio(); timerBeep(s, settings.timerVolume); }}>
            {SOUND_NAMES[alertKind(s)]}
          </Button>
        ))}
      </Group>
    </Stack>
  );
};

/** Speaker button in the timer strip: opens the same editor. */
export const TimerSoundMenu = ({ size }: { size: 'md' | 'lg' }) => {
  const deps = useDeps();
  const settings = useLiveQuery(() => deps.settings.get(), []);
  if (!settings) return null;
  const on = settings.timerAlertsSec.length > 0;
  const Icon = on ? IconVolume : IconVolumeOff;
  return (
    <Popover position="bottom-end" width={340} zIndex={500} withArrow shadow="md" onOpen={unlockAudio}>
      <Popover.Target>
        <Tooltip label={on ? `Timer beeps: ${settings.timerAlertsSec.map(label).join(', ')}` : 'Timer sound off'}>
          <ActionIcon size={size} aria-label="Timer sound" data-testid="timer-sound"><Icon size={16} /></ActionIcon>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown>
        <TimerAlertsEditor />
      </Popover.Dropdown>
    </Popover>
  );
};
